import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import { theme } from '../../themes/theme'
import { MUTED_TEXT } from '../styles/colors'

const inputField = theme.colors.inputField

const sizes = {
  small: { padding: '6px 10px', fontSize: '14px', lineHeight: '18px' },
  medium: { padding: '10px 15px', fontSize: '14px', lineHeight: '20px' }
}

const Field = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  background: ${inputField.background};
  border: 1px solid ${props => (props.error ? theme.colors.error : props.focused ? theme.colors.primary : inputField.border)};
  border-radius: ${theme.borderRadius.input};
  box-sizing: border-box;
  transition: border-color 0.12s ease;

  &:focus-within {
    border-color: ${props => (props.error ? theme.colors.error : theme.colors.primary)};
  }
`

const Input = styled.input`
  flex: 1 1 0%;
  width: 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${inputField.text};
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: ${props => sizes[props.size].fontSize};
  line-height: ${props => sizes[props.size].lineHeight};
  padding: ${props => sizes[props.size].padding};

  ::placeholder {
    color: ${inputField.placeholder};
  }

  :disabled {
    color: ${inputField.disabledText};
  }
`

const Extension = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding-right: 10px;
  color: ${MUTED_TEXT};
  font-family: ${theme.fonts.body};
  font-size: ${props => sizes[props.size].fontSize};
  line-height: ${props => sizes[props.size].lineHeight};
  user-select: none;

  .MuiSvgIcon-root {
    width: 13px;
    height: 13px;
  }
`

// Controlled filename input with a locked extension.
function FilenameInput({
  value,
  extension = '',
  onChange = () => {},
  onKeyDown = () => {},
  onBlur = () => {},
  error = false,
  disabled = false,
  autoFocus = false,
  size = 'medium',
  id,
  ariaLabel = 'File name',
  ariaDescribedBy,
  placeholder = 'File name'
}) {
  return (
    <Field error={error} data-testid="filename-input">
      <Input
        id={id}
        type="text"
        value={value}
        size={size}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={ariaDescribedBy}
        autoComplete="off"
        spellCheck={false}
      />
      {extension && (
        <Extension size={size} data-testid="filename-extension" title="The extension cannot be changed">
          <LockOutlinedIcon aria-hidden="true" />
          {extension}
        </Extension>
      )}
    </Field>
  )
}

FilenameInput.propTypes = {
  value: PropTypes.string.isRequired,
  extension: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  placeholder: PropTypes.string
}

export default FilenameInput
